import { Search, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import type { SearchOperator } from '@/types/filters';

const OPERATOR_OPTIONS: { value: SearchOperator; label: string }[] = [
  { value: 'contains', label: 'enthält' },
  { value: 'equals', label: 'gleich' },
  { value: 'not_contains', label: 'enthält nicht' },
];

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onSearch: () => void;
  onClear: () => void;
  operator: SearchOperator;
  onOperatorChange: (operator: SearchOperator) => void;
  placeholder?: string;
  children?: ReactNode;
}

export function SearchBar({
  value,
  onChange,
  onSearch,
  onClear,
  operator,
  onOperatorChange,
  placeholder = 'Suchen...',
  children,
}: SearchBarProps) {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') onSearch();
  };

  return (
    <div className="bg-white border border-table-border rounded-lg overflow-hidden w-full">
      <div className="flex items-center gap-3 w-full p-4">
        <Select
          aria-label="Suchoperator"
          value={operator}
          onChange={(e) => onOperatorChange(e.target.value as SearchOperator)}
          options={OPERATOR_OPTIONS}
          className="w-40 flex-shrink-0"
        />

        <div className="flex-1 relative">
          <input
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent pr-10"
          />

          {value && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
              onClick={onClear}
              aria-label="Suche löschen"
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>

        <Button
          onClick={onSearch}
          disabled={!value.trim()}
          className="flex-shrink-0"
        >
          <Search className="h-4 w-4 mr-2" />
          Suchen
        </Button>
      </div>
      {children}
    </div>
  );
}
